import { useEffect } from "react";
import { findGridNeighbor } from "../../lib/findGridNeighbor";
import { isEditableTarget } from "../../lib/isEditableTarget";
import { recallStepFile } from "../../lib/lastOpenStepFile";
import { navigate, routes } from "../../lib/urls";

type Layout = Map<string, { row: number; col: number }>;
type Step = { id: string; canonicalId: string };

function findTopLeftStepId(layout: Layout): string | undefined {
  let best: { id: string; row: number; col: number } | undefined;
  for (const [id, pos] of layout) {
    if (
      !best ||
      pos.row < best.row ||
      (pos.row === best.row && pos.col < best.col)
    ) {
      best = { id, ...pos };
    }
  }
  return best?.id;
}

export function useStepMapKeyboardNav({
  layout,
  steps,
  selectedStep,
  spaceId,
  workflowId,
  isExpanded,
}: {
  layout: Layout;
  steps: Step[];
  selectedStep: string | null;
  spaceId: string;
  workflowId: string;
  isExpanded: boolean;
}) {
  useEffect(() => {
    const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

    // Navigate using stable identifier
    const selectStep = (stableId: string) =>
      navigate(
        routes.space.workflow,
        {
          spaceId,
          workflowId,
          step: stableId,
          file: stableId ? (recallStepFile(workflowId, stableId) ?? "") : "",
        },
        { replace: true }
      );

    // Convert physical ID to stable identifier
    const toStableId = (physicalId: string) =>
      steps.find((s) => s.id === physicalId)?.canonicalId;

    // Resolve current stable ID to physical ID for layout lookup
    const selectedStepId = steps.find(
      (s) => s.canonicalId === selectedStep
    )?.id;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return;

      if (e.key === "Escape") {
        if (selectedStep) {
          selectStep("");
        } else if (isExpanded) {
          navigate(null, { map: "" });
        }
        return;
      }

      if (e.key === "Enter" && isExpanded && selectedStep) {
        // Without preventDefault a focused link's native Enter activation
        // fires after this navigation and can immediately re-expand.
        e.preventDefault();
        navigate(null, { map: "" });
        return;
      }

      if (!arrowKeys.includes(e.key)) return;
      e.preventDefault();

      const targetPhysicalId = selectedStepId
        ? findGridNeighbor(layout, selectedStepId, e.key)
        : findTopLeftStepId(layout);
      const targetStableId = targetPhysicalId
        ? toStableId(targetPhysicalId)
        : undefined;
      if (targetStableId) selectStep(targetStableId);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedStep, steps, layout, spaceId, workflowId, isExpanded]);
}
